import {
  fetch_aml_result,
  fetch_credit_report_result,
  fetch_idv_result,
  fetch_latest_kyc_result,
  fetch_manual_verification_history,
  fetch_manual_verification_status,
  fetch_onboarding_services,
  fetch_verification_logs
} from '@/services/onboarding_journey_service';
import {
  attach_case_workflow_stage,
  build_case_checks,
  build_verification_statuses_by_form,
  get_bound_services,
  get_verification_binding_fallback
} from './manage_verification_helpers';
import { fetch_entity_type_verification_bindings } from '@/services/onboarding_verification_service';
import {
  get_verification_impact,
  get_verification_status_tone,
  normalize_verification_type,
  title_case
} from '@/services/onboarding_verification_common_helpers';

function normalize_stage_value(value) {
  return String(value || '').trim().toLowerCase();
}

function stage_is_manual_verification(stage = '') {
  return stage.includes('manual') && stage.includes('verif');
}

function stage_is_field_verification(stage = '') {
  return stage.includes('field') && stage.includes('verif');
}

function stage_is_verification(stage = '') {
  return stage.includes('verif');
}

function get_case_stage_sources(case_item = {}) {
  return [
    case_item?.current_workflow_status,
    case_item?.workflow_display_stage,
    case_item?.stage,
    case_item?.detail,
    case_item?.application_status,
    case_item?.status,
    case_item?.status_name
  ];
}

function get_case_stage_values(case_item = {}) {
  return get_case_stage_sources(case_item).map(normalize_stage_value).filter(Boolean);
}

function case_is_manual_verification(case_item = {}) {
  return get_case_stage_values(case_item).some(stage_is_manual_verification);
}

function case_is_verification(case_item = {}) {
  return get_case_stage_values(case_item).some(stage_is_verification);
}

function case_is_field_verification(case_item = {}) {
  return get_case_stage_values(case_item).some(stage_is_field_verification);
}

function get_case_placeholder_mode(case_item = {}) {
  return case_is_manual_verification(case_item) ? 'manual' : 'auto';
}

function get_case_placeholder_check_label(verification_mode) {
  if (verification_mode === 'manual') return 'Manual verification gate';
  if (verification_mode === 'field') return 'Field verification gate';
  return 'Verification gate';
}

function get_case_placeholder_impact(verification_mode) {
  if (verification_mode === 'manual') return 'Needs manual review';
  if (verification_mode === 'field') return 'Needs field review';
  return 'Needs review';
}

function get_case_id(case_item = {}) {
  return case_item?.application_id || case_item?.id || case_item?.entity_profile_id || Date.now();
}

function build_case_gate_placeholder_check(case_item = {}, is_manual_page = false) {
  const verification_mode = typeof is_manual_page === 'string'
    ? is_manual_page === 'field-verification' ? 'field' : is_manual_page === 'manual' ? 'manual' : get_case_placeholder_mode(case_item)
    : is_manual_page ? 'manual' : get_case_placeholder_mode(case_item);
  const check = get_case_placeholder_check_label(verification_mode);

  return {
    id: `${verification_mode}-verification-stage-${get_case_id(case_item)}`,
    check,
    case_name: case_item?.name || 'Onboarded Entity',
    source: 'Workflow gate',
    status: 'Pending',
    status_tone: 'warning',
    impact: get_case_placeholder_impact(verification_mode),
    last_run: '-',
    application_form_id: null,
    service_id: null,
    verification_mode,
    is_gate_placeholder: true,
    is_runnable: false,
    case_item
  };
}

function case_matches_verification_page(case_item = {}, workspace_mode = 'verification') {
  if (!case_is_verification(case_item)) return false;

  const is_manual_case = case_is_manual_verification(case_item);
  const is_field_case = case_is_field_verification(case_item);

  if (workspace_mode === 'manual') return is_manual_case;
  if (workspace_mode === 'field-verification') return is_field_case;
  return !is_manual_case && !is_field_case;
}

function case_matches_auto_page(case_item = {}) {
  return case_matches_verification_page(case_item, 'verification');
}

function case_matches_manual_page(case_item = {}) {
  return case_matches_verification_page(case_item, 'manual');
}

function case_matches_field_page(case_item = {}) {
  return case_matches_verification_page(case_item, 'field-verification');
}

function build_placeholder_for_auto(case_item) {
  return build_case_gate_placeholder_check(case_item, false);
}

function build_placeholder_for_manual(case_item) {
  return build_case_gate_placeholder_check(case_item, true);
}

export function build_lightweight_case_checks(cases = [], is_manual_page = false) {
  const workspace_mode = typeof is_manual_page === 'string'
    ? is_manual_page
    : (is_manual_page ? 'manual' : 'verification');
  const matcher = workspace_mode === 'manual'
    ? case_matches_manual_page
    : workspace_mode === 'field-verification'
      ? case_matches_field_page
      : case_matches_auto_page;
  const builder = workspace_mode === 'manual' || workspace_mode === 'field-verification'
    ? build_placeholder_for_manual
    : build_placeholder_for_auto;

  return cases.filter(matcher).map(builder);
}

function is_positive_number(value) {
  return Number(value || 0) > 0;
}

function get_first_numeric_id(values = []) {
  return Number(values.find(is_positive_number) || 0) || null;
}

function get_case_sub_entity_type_id(case_item = {}) {
  return get_first_numeric_id([
    case_item?.sub_entity_type_id,
    case_item?.profile?.sub_entity_type_id
  ]);
}

export function get_case_forms(case_item = {}) {
  const detailed_forms = Array.isArray(case_item?.detailed_forms) ? case_item.detailed_forms : [];
  if (detailed_forms.length) return detailed_forms;

  return Array.isArray(case_item?.forms) ? case_item.forms : [];
}

function empty_array() {
  return [];
}

function empty_object() {
  return {};
}

function has_manual_bindings(binding_data = {}) {
  return Array.isArray(binding_data?.manual_bindings) && binding_data.manual_bindings.length > 0;
}

async function fetch_verification_binding_data(entity_type_id, binding_options) {
  const scoped_data = await fetch_entity_type_verification_bindings(entity_type_id, binding_options)
    .catch(get_verification_binding_fallback);
  const has_scoped_manual_bindings = has_manual_bindings(scoped_data);
  const has_scoped_options = Boolean(binding_options?.project_id || binding_options?.sub_entity_type_id);
  if (has_scoped_manual_bindings || !has_scoped_options) {
    return scoped_data;
  }

  const unscoped_data = await fetch_entity_type_verification_bindings(entity_type_id)
    .catch(get_verification_binding_fallback);
  return has_manual_bindings(unscoped_data) ? unscoped_data : scoped_data;
}

export function build_binding_options(case_item) {
  return {
    sub_entity_type_id: get_case_sub_entity_type_id(case_item)
  };
}

function build_hydration_context(case_item, fetched_data) {
  const services = fetched_data[0];
  const verification_binding_data = fetched_data[1];
  const bound_services = get_bound_services(verification_binding_data, services);
  const case_with_stage = attach_case_workflow_stage(case_item, bound_services, verification_binding_data);

  return {
    case_with_stage,
    services,
    bound_services,
    verification_statuses_by_form: fetched_data[2],
    verification_logs: fetched_data[3],
    manual_history: fetched_data[4],
    manual_status_snapshot: fetched_data[5],
    latest_kyc_result: fetched_data[6],
    aml_result: fetched_data[7],
    credit_report_result: fetched_data[8],
    idv_result: fetched_data[9]
  };
}

function is_kyc_check(item = {}) {
  const values = [
    item?.check,
    item?.service?.name,
    item?.service?.service_name,
    item?.service?.verification_type
  ].map((value) => normalize_verification_type(value)).filter(Boolean);

  return values.some((value) => value.includes('kyc'));
}

function is_aml_check(item = {}) {
  const values = [
    item?.check,
    item?.service?.name,
    item?.service?.service_name,
    item?.service?.verification_type
  ].map((value) => normalize_verification_type(value)).filter(Boolean);

  return values.some((value) => value.includes('aml'));
}

function is_credit_report_check(item = {}) {
  const values = [
    item?.check,
    item?.service?.name,
    item?.service?.service_name,
    item?.service?.verification_type
  ].map((value) => normalize_verification_type(value)).filter(Boolean);

  return values.some((value) => value.includes('credit'));
}

function is_idv_check(item = {}) {
  const values = [
    item?.check,
    item?.service?.name,
    item?.service?.service_name,
    item?.service?.verification_type
  ].map((value) => normalize_verification_type(value)).filter(Boolean);

  return values.some((value) => (
    value.includes('identity')
    || value.includes('id verification')
    || value.includes('idv')
  ));
}

function merge_verification_result_into_checks(checks = [], result = null, matcher = () => false) {
  if (!result || typeof result !== 'object') {
    return checks;
  }

  const status = title_case(result?.status || 'Pending');
  const status_tone = get_verification_status_tone(result?.status);
  const impact = String(result?.message || '').trim() || get_verification_impact(status_tone);

  return checks.map((item) => {
    if (item?.verification_mode !== 'auto' || !matcher(item)) {
      return item;
    }

    return {
      ...item,
      status,
      status_tone,
      impact,
      raw_status: {
        ...(item?.raw_status && typeof item.raw_status === 'object' ? item.raw_status : {}),
        ...result,
        status: result?.status || item?.raw_status?.status || item?.status,
        message: result?.message || item?.raw_status?.message || item?.impact
      }
    };
  });
}

function merge_special_verification_results_into_checks(checks = [], context = {}) {
  const with_kyc = merge_verification_result_into_checks(checks, context.latest_kyc_result, is_kyc_check);
  const with_aml = merge_verification_result_into_checks(with_kyc, context.aml_result, is_aml_check);
  const with_idv = merge_verification_result_into_checks(with_aml, context.idv_result, is_idv_check);
  return merge_verification_result_into_checks(with_idv, context.credit_report_result, is_credit_report_check);
}

function find_identity_service_id(bound_services = []) {
  const identity_service = bound_services.find((service) => {
    const normalized_name = normalize_verification_type(
      service?.name || service?.service_name || service?.verification_type
    );
    return normalized_name.includes('identity')
      || normalized_name.includes('id verification')
      || normalized_name.includes('idv');
  });

  return Number(identity_service?.id || identity_service?.service_id || 0) || null;
}

export function build_case_detail_checks(case_item, forms, fetched_data, manual_mapping_by_link = {}) {
  const context = build_hydration_context(case_item, fetched_data);
  const checks = build_case_checks({
    case_with_stage: context.case_with_stage,
    forms,
    verification_logs: context.verification_logs,
    verification_statuses_by_form: context.verification_statuses_by_form,
    services: context.services,
    bound_services: context.bound_services,
    manual_status_snapshot: context.manual_status_snapshot,
    manual_history: context.manual_history,
    manual_mapping_by_link
  });

  return merge_special_verification_results_into_checks(checks, context);
}

export function build_load_promises(entity_profile_id, entity_type_id, binding_options, forms) {
  const services_promise = fetch_onboarding_services().catch(empty_array);
  const binding_data_promise = fetch_verification_binding_data(entity_type_id, binding_options);
  const idv_result_promise = Promise.all([services_promise, binding_data_promise])
    .then(([services, verification_binding_data]) => {
      const bound_services = get_bound_services(verification_binding_data, services);
      const identity_service_id = find_identity_service_id(bound_services);
      return fetch_idv_result(entity_profile_id, identity_service_id);
    })
    .catch(() => null);

  return [
    services_promise,
    binding_data_promise,
    build_verification_statuses_by_form(forms).catch(empty_object),
    fetch_verification_logs(entity_profile_id).catch(empty_array),
    fetch_manual_verification_history(entity_profile_id).catch(empty_array),
    fetch_manual_verification_status(entity_profile_id).catch(empty_object),
    fetch_latest_kyc_result(entity_profile_id).catch(() => null),
    fetch_aml_result(entity_profile_id).catch(() => null),
    fetch_credit_report_result(entity_profile_id).catch(() => null),
    idv_result_promise
  ];
}

export function update_all_filter_count(filters, count) {
  return filters.map(function update_filter(filter) {
    return filter.tone === 'all' ? { ...filter, count: String(count) } : filter;
  });
}

export function replace_case_checks(existing_checks, checks, case_item, matches_case) {
  const retained_checks = existing_checks.filter(function keep_check(item) {
    return !matches_case(item?.case_item, case_item);
  });

  return [...retained_checks, ...checks];
}
